import React, { useState, useEffect } from 'react';
import { useNoteTypes, useNoteTypeStatistics, useAlert } from '../../../../hooks';
import { FaPlus } from 'react-icons/fa';
import NoteTypeForm from './NoteTypeForm';
import NoteTypeList from './NoteTypeList';
import NoteTypeStatistics from './NoteTypeStatistics';
import { MODULE_DISPLAY_NAMES } from '../../../../services/noteTypeService';
import styles from './NoteTypes.module.css';

const NoteTypes = () => {
    const [showForm, setShowForm] = useState(false);
    const [editingNoteType, setEditingNoteType] = useState(null);
    const [filterModule, setFilterModule] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const { setInfoAlert, setErrorAlert, setIsModalOpen} = useAlert();

    const {
        noteTypes,
        loading,
        error,
        createNoteType,
        updateNoteType,
        deleteNoteType,
        updateModuleAvailability,
        refetch
    } = useNoteTypes();

    const {
        statistics,
        loading: statsLoading,
        refetch: refetchStats
    } = useNoteTypeStatistics();

    // Track modal state for alert positioning and header blocking
    useEffect(() => {
        if (showForm) {
            document.body.setAttribute('data-modal-open', 'true');
            setIsModalOpen(true);
        } else {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        }
        return () => {
            document.body.removeAttribute('data-modal-open');
            setIsModalOpen(false);
        };
    }, [showForm, setIsModalOpen]);

    // Handle create/edit form submission
    const handleFormSubmit = async (formData) => {
        // Store current scroll position
        const scrollPosition = window.scrollY;
        
        try {
            if (editingNoteType) {
                await updateNoteType(editingNoteType.noteTypeId, formData);
                setInfoAlert('Note type updated successfully');
            } else {
                await createNoteType(formData);
                setInfoAlert('Note type created successfully');
            }
            setShowForm(false);
            setEditingNoteType(null);
            await refetchStats(); // Refresh statistics
            
            // Restore scroll position after state updates and re-render
            setTimeout(() => window.scrollTo(0, scrollPosition), 0);
        } catch (error) {
            setErrorAlert(error.message || 'Operation failed');
        }
    };

    // Handle edit
    const handleEdit = (noteType) => {
        setEditingNoteType(noteType);
        setShowForm(true);
    };

    // Handle delete
    const handleDelete = async (noteType) => {
        if (window.confirm(`Are you sure you want to delete "${noteType.noteTypeName}"?`)) {
            // Store current scroll position
            const scrollPosition = window.scrollY;
            
            try {
                await deleteNoteType(noteType.noteTypeId);
                setInfoAlert('Note type deleted successfully');
                await refetchStats(); // Refresh statistics
                
                // Restore scroll position after state updates and re-render
                setTimeout(() => window.scrollTo(0, scrollPosition), 0);
            } catch (error) {
                setErrorAlert(error.message || 'Failed to delete note type');
                // Trigger a refetch to clear the error state and reload the list
                // This ensures the UI returns to normal view after showing the error
                await refetch();
            }
        }
    };

    // Cancel form
    const handleCancel = () => {
        setShowForm(false);
        setEditingNoteType(null);
    };

    // Filter note types by module and search query
    const filteredNoteTypes = noteTypes
        .filter(nt => {
            // First apply module filter if set
            if (filterModule) {
                const propertyName = filterModule.charAt(0).toLowerCase() + filterModule.slice(1);
                if (!nt[propertyName]) return false;
            }
            
            // Then apply search filter if set
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                return nt.noteTypeName.toLowerCase().includes(query) ||
                       (nt.noteTypeDesc && nt.noteTypeDesc.toLowerCase().includes(query));
            }
            
            return true;
        });

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2>Note Types Management</h2>
                    <p>Manage note types and their visibility across different modules.</p>
                </div>

                <div className={styles.error}>
                    <h3>Error Loading Note Types</h3>
                    <p>{error}</p>
                    <div className={styles.errorActions}>
                        <button onClick={refetch} className={styles.retryButton}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Note Types Management</h2>
                <p>Manage note types and their visibility across different modules.</p>
            </div>

            {/* Statistics */}
            <NoteTypeStatistics 
                statistics={statistics}
                loading={statsLoading}
            />

            {/* Controls */}
            <div className={styles.controls}>
                <button
                    onClick={() => setShowForm(true)}
                    className={styles.addButton}
                    disabled={loading}
                >
                    <FaPlus className={styles.buttonIcon} /> Create New Note Type
                </button>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <NoteTypeForm
                            noteType={editingNoteType}
                            onSubmit={handleFormSubmit}
                            onCancel={handleCancel}
                            loading={loading}
                        />
                    </div>
                </div>
            )}

            {/* Note Types List */}
            <NoteTypeList
                noteTypes={filteredNoteTypes}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                filterModule={filterModule}
                setFilterModule={setFilterModule}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
            />
        </div>
    );
};

export default NoteTypes;
